import React from 'react';
import ReactDOM from 'react-dom/client';
import { inject, observer } from 'mobx-react';
import H5Toast from './h5';
import PCToast from './pc';
import type { AppStore } from '@stores/app/store';

export type ToastType = 'success' | 'error' | 'info' | 'loading';

interface ToastProps {
    app?: AppStore;
    message: string;
    type?: ToastType;
    duration?: number;
    onClose?: () => void;
    icon?: string; // 自定义图标URL
    showIcon?: boolean;
}

interface ToastConfig {
    content: string;
    duration?: number;
    onClose?: () => void;
    icon?: string; // 自定义图标URL
    showIcon?: boolean;
}

interface MiniToastOptions {
    title?: string;
    icon?: 'success' | 'error' | 'info' | 'loading' | 'none';
    duration?: number;
    onClose?: () => void;
}

interface ToastInstance {
    root: ReactDOM.Root;
    container: HTMLElement;
    close: () => void;
    type: ToastType;
}

@inject('app')
@observer
class ToastComponent extends React.Component<ToastProps> {
    static defaultProps = {
        type: 'success' as ToastType,
        duration: 2000,
    };

    render() {
        const { app, ...props } = this.props;
        const platform = app?.platform || 'h5';

        if (platform === 'pc') {
            return <PCToast {...props} />;
        } else {
            return <H5Toast {...props} />;
        }
    }
}

// 获取平台类型
const getPlatform = () => {
    if (typeof window === 'undefined') return 'h5';
    const ua = window.navigator.userAgent.toLowerCase();
    return /mobile|android|iphone|ipad|phone/i.test(ua) ? 'h5' : 'pc';
};

// 全局 Toast 实例栈，管理所有活跃的 Toast
const toastStack: ToastInstance[] = [];

// 记录最后一次 showLoading 的关闭函数，方便 hideLoading
let lastLoadingCloser: null | (() => void) = null;

const normalizeDuration = (duration?: number) => {
    if (duration === undefined || duration === null) return undefined;
    if (!Number.isFinite(duration)) return Infinity;
    return duration;
};

const hideLastByType = (type: ToastType) => {
    for (let i = toastStack.length - 1; i >= 0; i--) {
        if (toastStack[i].type === type) {
            toastStack[i].close();
            break;
        }
    }
};

const hideAllInternal = () => {
    // 复制一份，避免 close 时同步 splice 影响遍历
    const all = toastStack.slice();
    all.forEach(t => t.close());
};

// 显示Toast的核心函数
const showToast = (config: ToastConfig, type: ToastType): (() => void) => {
    // 小程序风格：同一时间只显示一个 toast
    hideAllInternal();
    lastLoadingCloser = null;

    const container = document.createElement('div');
    document.body.appendChild(container);

    const platform = getPlatform();
    const Component = platform === 'pc' ? PCToast : H5Toast;

    const root = ReactDOM.createRoot(container);

    let closed = false;

    const closeToast = () => {
        if (closed) return;
        closed = true;

        // 同步从栈中移除，避免 hideAll 等 while 循环卡住
        const index = toastStack.findIndex(t => t.container === container);
        if (index > -1) {
            toastStack.splice(index, 1);
        }

        if (type === 'loading' && lastLoadingCloser === closeToast) {
            lastLoadingCloser = null;
        }

        config?.onClose?.();
        setTimeout(() => {
            root?.unmount();
            // 只在还挂载在 DOM 时才移除，避免 NotFoundError
            if (container?.parentNode) {
                container.parentNode.removeChild(container);
            }
        }, 100);
    };

    root.render(
        <Component
            message={config?.content || '网络开小差了～'}
            type={type}
            duration={config?.duration || 2000}
            onClose={closeToast}
            icon={config?.icon}
            showIcon={config?.showIcon}
        />
    );

    // 创建 Toast 实例
    const toastInstance: ToastInstance = {
        root,
        container,
        close: closeToast,
        type,
    };

    // 添加到栈中
    toastStack.push(toastInstance);

    if (type === 'loading') {
        lastLoadingCloser = closeToast;
    }

    return closeToast;
};

// 静态方法
const Toast = {
    success: (config: ToastConfig | string) => {
        const cfg = typeof config === 'string' ? { content: config } : config;
        return showToast(cfg, 'success');
    },
    error: (config: ToastConfig | string) => {
        const cfg = typeof config === 'string' ? { content: config } : config;
        return showToast(cfg, 'error');
    },
    info: (config: ToastConfig | string) => {
        const cfg = typeof config === 'string' ? { content: config } : config;
        return showToast(cfg, 'info');
    },
    loading: (config: ToastConfig | string) => {
        const cfg = typeof config === 'string' ? { content: config } : config;
        // loading 默认不自动关闭，duration 设为 Infinity
        return showToast({ ...cfg, duration: cfg.duration || Infinity }, 'loading');
    },
    showToast: (options: MiniToastOptions | string) => {
        const opts: MiniToastOptions = typeof options === 'string' ? { title: options } : (options || {});
        const icon = opts.icon || 'none';
        const title = opts.title || '';
        const duration = normalizeDuration(opts.duration);

        if (icon === 'loading') {
            return showToast({ content: title || '加载中...', duration: duration ?? Infinity, onClose: opts.onClose }, 'loading');
        }

        // none 走 info（不显示图标）目前组件里没有“无图标”样式，先用 info 走一套
        const mappedType: ToastType = (icon === 'none' ? 'info' : (icon as ToastType));
        return showToast({
            content: title,
            duration: duration ?? 2000,
            onClose: opts.onClose,
            showIcon: icon !== 'none',
        }, mappedType);
    },
    // 兼容旧用法：不推荐，建议统一用 showToast({icon:'loading'}) / hideToast()
    showLoading: (options: MiniToastOptions | string) => {
        return Toast.showToast(typeof options === 'string' ? { title: options, icon: 'loading' } : { ...(options || {}), icon: 'loading' });
    },
    hideLoading: () => {
        Toast.hideToast();
    },
    hideToast: () => {
        const lastToast = toastStack[toastStack.length - 1];
        if (lastToast) lastToast.close();
    },
    // 关闭最后一个 Toast（通常用于关闭 loading）
    hide: () => {
        const lastToast = toastStack[toastStack.length - 1];
        if (lastToast) {
            lastToast.close();
        }
    },
    // 关闭所有 Toast
    hideAll: () => {
        // 从后往前关闭，避免数组操作冲突
        while (toastStack.length > 0) {
            const toast = toastStack[toastStack.length - 1];
            if (toast) {
                toast.close();
            }
        }
    },
    // 关闭指定类型的最后一个 Toast
    hideByType: (type: ToastType) => {
        hideLastByType(type);
    },
};

export default Toast;
export { ToastComponent };
